import React, { useEffect, useRef, useState } from "react";
import { RefreshCw, ArrowDown } from "lucide-react";
import { isTouch } from "../lib/pwa";

const THRESH = 72;
let active = 0;
const setPtrClass = (on) => { active = Math.max(0, active + (on ? 1 : -1)); document.documentElement.classList.toggle("ptr-active", active > 0); };

// Touch-only pull-to-refresh for document-level scrolling pages. onRefresh must return a promise.
export function PullToRefresh({ onRefresh, zh, children }) {
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const startY = useRef(null);
  const pullRef = useRef(0);
  const busyRef = useRef(false);

  useEffect(() => {
    if (!isTouch()) return;
    setPtrClass(true);
    const set = (v) => { pullRef.current = v; setPull(v); };
    const onStart = (e) => { startY.current = window.scrollY <= 0 && !busyRef.current ? e.touches[0].clientY : null; };
    const onMove = (e) => {
      if (startY.current == null) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0 || window.scrollY > 0) { if (pullRef.current) set(0); if (dy <= 0) startY.current = null; return; }
      if (dy > 8 && e.cancelable) e.preventDefault();
      set(Math.min(130, dy * 0.55));
    };
    const onEnd = async () => {
      if (startY.current == null) return;
      startY.current = null;
      if (pullRef.current >= THRESH) {
        busyRef.current = true; setBusy(true); set(THRESH);
        const started = Date.now();
        try { await onRefresh?.(); } catch {}
        await new Promise((r) => setTimeout(r, Math.max(0, 500 - (Date.now() - started))));
        busyRef.current = false; setBusy(false);
      }
      set(0);
    };
    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd);
    document.addEventListener("touchcancel", onEnd);
    return () => {
      setPtrClass(false);
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, [onRefresh]);

  const show = pull > 0 || busy;
  const ready = pull >= THRESH;
  return (
    <>
      {show && (
        <div data-testid="ptr-indicator" data-busy={busy ? "1" : "0"} className="ptr-indicator" style={{ transform: `translate(-50%, ${Math.min(pull, THRESH) - 44}px)`, opacity: Math.min(1, pull / 40) }}>
          <div className={`h-10 w-10 rounded-full bg-white shadow-lift border border-slate-200/80 flex items-center justify-center ${ready || busy ? "text-brand-600" : "text-slate-400"}`}>
            {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ArrowDown className="h-4 w-4 transition-transform" style={{ transform: `rotate(${ready ? 180 : 0}deg)` }} />}
          </div>
          <span className="sr-only">{busy ? (zh ? "正在刷新" : "Refreshing") : ready ? (zh ? "松开刷新" : "Release to refresh") : (zh ? "下拉刷新" : "Pull to refresh")}</span>
        </div>
      )}
      <div style={pull ? { transform: `translateY(${Math.min(pull, THRESH) * 0.6}px)`, transition: busy ? "transform .2s" : "none" } : undefined}>{children}</div>
    </>
  );
}

export default PullToRefresh;
